import maplibregl from 'maplibre-gl';

import chroma from "chroma-js";
import * as c from "../common.js";
import * as boatdata from "../domain/boat-data.js";
import moment from 'moment';
import * as mapDist from "./map-distance-tool.js";
import { SEPARATION_MODE } from "./TrackAnimator.js";
import { distance } from '@turf/turf';
import { SailTrack } from "../domain/Race.js";
import * as geom from "../utils/geometry-util.js";
import * as mapDebug from "../utils/map-debug.js";
import * as util from "../utils/utils.js";

const map = new maplibregl.Map({
    container: 'map',
    style: {
        version: 8,
        sources: {
            'osm': {
                type: 'raster',
                tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
                tileSize: 256,
                maxzoom: 19, //OSM has max zoom 19
                attribution: '© OpenStreetMap contributors'
            },
            'terrarium': {
                type: 'raster-dem',
                tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
                encoding: 'terrarium',
                tileSize: 256
            }
        },
        layers: [
            {
                id: 'osm',
                type: 'raster',
                source: 'osm'
            }
        ],
        // terrain: 
        // {
           // source: 'terrarium',
           // exaggeration: TERRAIN_EXAGGERATION
        // }
    },
    center: [-98.5, 39.8],
    zoom: 3,
    maxZoom: 19,
    maxPitch: 80
});

let _animator;
let _race;
let _mapView = null;

export function setMapView(mapView) {
    _mapView = mapView;
}

const FASTEST_TIME_SKIP_RATE = 40 * 60;
const SLOWEST_TIME_SKIP_RATE = 10;
let _mapUIState = {
    isDistToolSelected: false,
    isCourseEditingActive: false,
    isDisablePlayer: false
};

export function getCurrentPlayTime() {
    return _animator ? _animator.getCurrentPlayTime() : moment(0);
}

export function setCurrentPlayTime(ts) {
    if (_animator) {
        _animator.setCurrentPlayTime(ts);
    }
}

export function getTimeIndex(data, tsSeconds) {
    const p = moment(tsSeconds);
    return data.findIndex((e) => e.t.isSameOrAfter(p));
}

export function getMap() {
    return map;
}

export function updateSize() {
    map.resize();
}

export function update() {
    if (_animator && !c.isUIState("MAP", c.UI_STATE.MAP.PLAYING)) {
        _animator.update();
    }
}

let _postAnimateFunction = null;
let _fpsFrameCount = 0;
let _fpsLastLogMs = 0;
export function setCourseEditingActive(active) {
    _mapUIState.isCourseEditingActive = active;
}

// Lets overlays (e.g. weather pin picking) claim a map click before it falls through to
// the default play/pause toggle — each interceptor is tried in order and can return true to consume it.
let _clickInterceptors = [];
export function registerClickInterceptor(fn) {
    _clickInterceptors.push(fn);
}
export function unregisterClickInterceptor(fn) {
    _clickInterceptors = _clickInterceptors.filter(f => f !== fn);
}

// TODO: Refactor CourseCreator and map-distance-tool to use stopPlayer() instead of their own state flags
export function disablePlayer(stopped) {
    _mapUIState.isDisablePlayer = stopped;
    if (stopped) {
        pauseAnimation();
    }
}

export function stopPlaying() {
    pauseAnimation();
}

export function initAnimator(newAnimator) {
    let playTime = null;
    let timeSkipRate = null;
    if (_animator) {
        playTime = _animator.getCurrentPlayTime();
        timeSkipRate = _animator.getTimeSkipRate();
        _animator.destroy();
    }

    const tracks = _race.sail().getTracks();
    for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        const color = track.getColor();
        track.setColor(color);
    }

    _animator = newAnimator;

    if (timeSkipRate != null) {
        _animator.setTimeSkipRate(timeSkipRate);
    }
    
    if (playTime != null)
        _animator.setCurrentPlayTime(playTime);
    _animator.setPostAnimateCallback((rankings) => {
        // logFPS();
        const progressBarPct = calcProgressBarPct(_animator.playTsSeconds);
        updateProgressBar(progressBarPct);
        mapDebug.renderAndClear();
        if (_postAnimateFunction) {
            _postAnimateFunction(rankings);
        }
    });
}


const clickHandler = function (evt) {
    if (_mapUIState.isCourseEditingActive) return;
    if (_clickInterceptors.some(fn => fn(evt))) return;
    console.log("map clicked");
    toggleAnimationPlaying();
}

const handleTooltipMove = function (evt) {
    const tooltip = getTrackToolTip(evt.lngLat);
    const tooltipEl = document.getElementById('map-tooltip');
    if (tooltip) {
        tooltipEl.textContent = tooltip;
        tooltipEl.style.display = 'block';
        tooltipEl.style.left = evt.originalEvent.pageX + 10 + 'px';
        tooltipEl.style.top = evt.originalEvent.pageY + 10 + 'px';
    } else {
        tooltipEl.style.display = 'none';
    }
};

function logFPS() {
    _fpsFrameCount++;
    const now = Date.now();
    if (now - _fpsLastLogMs >= 1000) {
        console.log(`FPS: ${_fpsFrameCount}`);
        _fpsFrameCount = 0;
        _fpsLastLogMs = now;
    }
}

export function initMap(postAnimateFunction, race, settings = null, animator) {
    console.log('initMap called - using MapLibre GeoJSON rendering');
    _race = race;
    _postAnimateFunction = postAnimateFunction;

    c.setUIState("MAP",c.UI_STATE.MAP.RESET);

    const tracks = race.sail().getTracks();
    for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        console.log("loading "+track.getShipName(settings));
        const color = track.getColor(settings);
        track.setColor(color);
    }

    initAnimator(animator);

    mapDebug.registerMap(map);
    map.off('click', clickHandler);
    map.on('click', clickHandler);
    
    const tooltipEl = document.getElementById('map-tooltip');
    map.off('mousemove', handleTooltipMove);
    map.on('mousemove', handleTooltipMove);
    map.off('mouseleave', () => tooltipEl.style.display = 'none');
    map.on('mouseleave', () => tooltipEl.style.display = 'none');
    
    map.off('zoom', update);
    map.on('zoom', update);
    map.off('move', update);
    map.on('move', update);


    setTimeSkipRate();
    resetAnimation();
    startAnimation();
}

function resetExtent() {
    if (!_race?.sail()?.getTracks()?.length) return;
    const { minLon, minLat, maxLon, maxLat } = _race.extent();
    map.fitBounds([[minLon, minLat], [maxLon, maxLat]], { padding: 40, duration: 0 });
}

function getTrackToolTip(lngLat) {
    if (!_race?.sail()) return null;
    
    let minDistance = geom.sizeMetersWithMin(map,5,1/200, lngLat.lat);;
    let closestData = null;
    
    _race.sail().getTracks().forEach(track => {
        if (!track.isVisible()) return;
        
        const frames = track.getFrames();
        for (let i = 0; i < track.getPlayIndex(); i++) {
            const coord = [frames[i].lon, frames[i].lat];
            const dist = distance([lngLat.lng, lngLat.lat], coord, { units: 'meters' });
            
            if (dist < minDistance) {
                minDistance = dist;
                closestData = { track: track, index: i };
            }
        }
    });
    
    if (closestData) {
        const frame = closestData.track.getFrames()[closestData.index];
        let tooltip = `${closestData.track.getShipName()}: SOG: ${frame.sog?.toFixed(1) || 'N/A'}`;
        if (frame.bs != null) tooltip += `, STW: ${frame.bs.toFixed(1)}`;
        if (_race.sail().frameTws(frame) != null) tooltip += `, TWS: ${_race.sail().frameTws(frame).toFixed(0)}`;
        if (frame._vmg != null) tooltip += `, VMG: ${frame._vmg.toFixed(1)}`;
        if (frame.calc?.polarPerf != null) tooltip += `, Pol: ${frame.calc.polarPerf}%`;
        return tooltip;
    }
    
    return null;
}

const WIND_BARB_MODES = {
    TRUE: "True",
    APPARENT: "Apparent",
    COLOR_SHIFT: "Shift",
    COLOR_PLAIN: "Plain"
};

const ctrlToggleWind = document.getElementById('ctrlToggleWind');
const windBarbDrawer = document.getElementById('windbarbDrawer');
ctrlToggleWind.addEventListener('click', () => {
    const enabled = ctrlToggleWind.classList.toggle('selected');
    _animator.setWindDrawMode(enabled);
});
let _windDrawerHideTimer = { id: null };
util.initialiseTimer(windBarbDrawer, windBarbDrawer, 800, _windDrawerHideTimer);
util.initialiseTimer(ctrlToggleWind, windBarbDrawer, 800, _windDrawerHideTimer);

let _SpeedDataDrawerHideTimer = { id: null };
const ctrlToggleSpeedData = document.getElementById('ctrlToggleSpeedData');
const speedDataDrawer = document.getElementById('speedDataDrawer');
util.initialiseTimer(speedDataDrawer, speedDataDrawer, 800, _SpeedDataDrawerHideTimer);
util.initialiseTimer(ctrlToggleSpeedData, speedDataDrawer, 800, _SpeedDataDrawerHideTimer);

/*
document.getElementById('btnModeColor').addEventListener('click', () => {
    const value = document.getElementById('btnModeColor').classList.toggle('selected');
    const mode = value ? WIND_BARB_MODES.COLOR_SHIFT : WIND_BARB_MODES.COLOR_PLAIN;
    _animator.setWindBarbColor(mode);
});*/

function selectWindMode(mode) {
    document.getElementById('btnModeTrue').classList.toggle('selected', mode === WIND_BARB_MODES.TRUE);
    document.getElementById('btnModeApparent').classList.toggle('selected', mode === WIND_BARB_MODES.APPARENT);
    _animator.setWindBarbMode(mode);
    // console.log("Selected Wind Mode:", mode);
}
document.getElementById('btnModeTrue').addEventListener('click', () => {
    selectWindMode(WIND_BARB_MODES.TRUE)
});
document.getElementById('btnModeApparent').addEventListener('click', () => {
    selectWindMode(WIND_BARB_MODES.APPARENT)
});

function resetAnimation() {
    if (!_animator) return;
    _animator.pause();
    _animator.reset();
    resetExtent();
}

function startAnimation() {
    if (!_animator) return;
    _animator.start();
    document.getElementById("map").focus();
}

function pauseAnimation() {
    if (!_animator) return;
    _animator.pause();
    document.getElementById("map").focus();
}

function toggleAnimationPlaying() {
    if (_mapUIState.isDisablePlayer) return;
    if (c.isUIState("TOOLS",c.UI_STATE.TOOLS.WAITING_FOR_USER_CLICK))
        return;

    if (c.isUIState("MAP", c.UI_STATE.MAP.PLAYING)) 
        pauseAnimation();
    else if (!c.isUIState("MAP",c.UI_STATE.MAP.PLAYING)) 
        startAnimation();
}

// Keyboard controls for pitch/tilt
document.addEventListener("keydown", function (event) {
    const activeElement = document.activeElement;
    const isInputField =
        activeElement.tagName === "INPUT" ||
        activeElement.tagName === "TEXTAREA" ||
        activeElement.isContentEditable;
    if (isInputField) return;

    switch (event.code) {
        case "Space":
            console.log("space pressed clicked");
            event.preventDefault();
            toggleAnimationPlaying();
            break;
        case "Comma":
            if (_animator) _animator.advanceFrame(-1);
            break;
        case "Period":
            if (_animator) _animator.advanceFrame(1);
            break;
        case "PageUp":
            event.preventDefault();
            const currentPitch = map.getPitch();
            map.setPitch(Math.min(currentPitch + 5, 85));
            console.log('Pitch:', map.getPitch());
            break;
        case "PageDown":
            event.preventDefault();
            const pitch = map.getPitch();
            map.setPitch(Math.max(pitch - 5, 0));
            console.log('Pitch:', map.getPitch());
            break;
    }
});

document.getElementById("play").onclick = () => {
    console.log("play clicked");
    startAnimation();
};

document.getElementById("pause").onclick = () => {
    console.log("pause clicked");
    pauseAnimation();
};

document.getElementById("reset").onclick = () => {
    resetAnimation();
};

document.getElementById("stepBack").onclick = () => {
    _animator.advanceFrame(-1);
};

document.getElementById("stepForward").onclick = () => {
    _animator.advanceFrame(1);
};

const playSpeedButton = document.getElementById('playSpeed');
const sliderContainer = document.getElementById('playSpeedSliderContainer');
const slider = document.getElementById('playSpeedSlider');

let sliderVisible = false;

playSpeedButton.addEventListener('click', (e) => {
  sliderVisible = !sliderVisible;
  sliderContainer.style.display = sliderVisible ? 'block' : 'none';
});

document.addEventListener('click', (e) => {
  if (!sliderContainer.contains(e.target) && !playSpeedButton.contains(e.target)) {
    sliderVisible = false;
    sliderContainer.style.display = 'none';
  }
});

slider.addEventListener('input', (e) => {
    setTimeSkipRate(e.target.value);
});

function setTimeSkipRate(playSpeedSlider) {
    let playSpeed;
    if (playSpeedSlider) {
        playSpeed = skewedNormalize(parseFloat(playSpeedSlider), 1, 100);
        const timeSkipRate = playSpeed * (FASTEST_TIME_SKIP_RATE - SLOWEST_TIME_SKIP_RATE) + SLOWEST_TIME_SKIP_RATE;
        _animator.setTimeSkipRate(timeSkipRate);
    } else {
        const timeSkipRate = (_animator.lastTsSeconds - _animator.firstTsSeconds) / 120;
        _animator.setTimeSkipRate(timeSkipRate);
        playSpeed = inverseSkewedNormalize(timeSkipRate / FASTEST_TIME_SKIP_RATE * 100);
        slider.value = playSpeed;
    }
    console.log('Play speed changed to:', playSpeed * 100, "%, time skip rate of:" + _animator.timeSkipRate);
}

function updateProgressBar(progressBarPct) {
    const progressBar = document.getElementById("progressBar");
    progressBar.value = progressBarPct * 10;
    progressBar.disabled = false;

    const currentTimeDiv = document.getElementById("currentTime");
    currentTimeDiv.textContent = `${getCurrentPlayTime().format('YY-MM-DD HH:mm:ss')}`;

    const foundIdx = _race.sail().data.vessel.data.findIndex((f) => f.t.unix() > _animator.playTsSeconds);
    if (foundIdx > 0) {
        const f = _race.sail().data.vessel.data[foundIdx];
        document.getElementById("vesselSTW").innerHTML = f.bs;
        document.getElementById("vesselSOG").innerHTML = f.sog;
        document.getElementById("vesselVMG").innerHTML = f._vmg != undefined ? f._vmg.toFixed(2) : '';
        document.getElementById("vesselTWA").innerHTML = `${boatdata.convert360North0To180PosNeg(_race.sail().frameTwa(f))}°`;
        document.getElementById("vesselTWS").innerHTML = _race.sail().frameTws(f).toFixed(0);
        document.getElementById("vesselAWA").innerHTML = `${boatdata.convert360North0To180PosNeg(f.awa)}°`;
        document.getElementById("vesselAWS").innerHTML = f.aws.toFixed(0);
        document.getElementById("vesselTWD").innerHTML = `${_race.sail().frameTwd(f) != null ? _race.sail().frameTwd(f) : ''}°`;
        document.getElementById("vesselTH").innerHTML = `${f.th != null ? f.th : ''}°`;
        document.getElementById("vesselCOG").innerHTML = `${f.cog != undefined ? f.cog : f._cog}°`;
        document.getElementById("vesselPolar").innerHTML = `${f.calc?.polarPerf != undefined ? f.calc?.polarPerf : ''}%`;
        document.getElementById("vesselHeel").innerHTML = `${f.r != undefined ? f.r : ''}°`;
        const leewayAbs = _race.sail().getLeewayAbs(foundIdx);
        document.getElementById("vesselLeeway").innerHTML = `${leewayAbs != null ? leewayAbs : ''}°`;
    } else {
        document.getElementById("vesselSTW").innerHTML = "";
        document.getElementById("vesselSOG").innerHTML = "";
        document.getElementById("vesselVMG").innerHTML = "";
        document.getElementById("vesselTWA").innerHTML = "";
        document.getElementById("vesselTWS").innerHTML = "";
        document.getElementById("vesselAWA").innerHTML = "";
        document.getElementById("vesselAWS").innerHTML = "";
        document.getElementById("vesselTWD").innerHTML = "";
        document.getElementById("vesselTH").innerHTML = "";
        document.getElementById("vesselCOG").innerHTML = "";
        document.getElementById("vesselPolar").innerHTML = "";
        document.getElementById("vesselHeel").innerHTML = "";
        document.getElementById("vesselLeeway").innerHTML = "";
    }
}

let _isScrubbing = false;

document.getElementById("progressBar").addEventListener("mousedown", () => {
    if (!_isScrubbing) {
        pauseAnimation();
        _mapView.setAutoCenterAllowed(false);
        _isScrubbing = true;
    }
});

document.getElementById("progressBar").addEventListener("input", (event) => {
    const index = parseInt(event.target.value, 10);
    const progressBarRatio = index / 1000;
    setCurrentPlayTime((progressBarRatio * (_animator.lastTsSeconds - _animator.firstTsSeconds) + _animator.firstTsSeconds)*1000);
});

document.getElementById("progressBar").addEventListener("change", () => {
    _isScrubbing = false;
    _mapView.setAutoCenterAllowed(true);
    update();
});

export function calculateSeparationMeters(track) {
    return _animator ? _animator.calculateSeparationLabel(track) : "-";
}

export function setFollowedVessel(track) {
    if (_animator) _animator.setFollowedVessel(track);
    _mapView.initViewMode();
}

export function getFollowedVessel() {
    return _animator ? _animator.getFollowedVessel() : null;
}

export function getOwnVessel() {
    return _animator ? _animator.sail.getVesselTrack() : null;
}

export function setTrackStyle(trackStyle) {
    if (_animator) {
        _animator.setTrackStyle(trackStyle);
    }
}

const ctrlSeparationMode = document.getElementById('ctrlSeparationMode');
ctrlSeparationMode.addEventListener('click', () => {
    const newMode = !_animator.showSeparationModeGraphics;
    _animator.setSeparationModeGraphics(newMode);
    ctrlSeparationMode.classList.toggle('selected', newMode);
});

const separationModeDrawer = document.getElementById('separationModeDrawer');
let _separationModeDrawerHideTimer = { id: null };
util.initialiseTimer(separationModeDrawer, separationModeDrawer, 800, _separationModeDrawerHideTimer)
util.initialiseTimer(ctrlSeparationMode, separationModeDrawer, 800, _separationModeDrawerHideTimer)


const btnModeTime = document.getElementById('btnModeTime');
const btnModeDist = document.getElementById('btnModeDist');
const btnModWind = document.getElementById('btnModeWind');
const btnModeWaypoint = document.getElementById('btnModeWaypoint');

function selectSeparationMode(mode) {
    btnModWind.classList.toggle('selected', mode === SEPARATION_MODE.WIND);
    btnModeDist.classList.toggle('selected', mode === SEPARATION_MODE.DIST);
    btnModeDist.disabled = false;
    btnModeWaypoint.classList.toggle('selected', mode === SEPARATION_MODE.WAYPOINT);
    if (mode == SEPARATION_MODE.WAYPOINT && !mapDist.isWayPointToolRunning()) {
        mapDist.addInteractiveWayPointTool(map);
        mapDist.setWayPointDragCallback(() => {
            if (_animator) _animator.update();
        });
    } else if (mode != SEPARATION_MODE.WAYPOINT && mapDist.isWayPointToolRunning()) {
        mapDist.removeInteractiveWayPointTool(map);
    }
    if (mode == SEPARATION_MODE.WIND || mode == SEPARATION_MODE.WAYPOINT) {
        _animator.setSeparationModeGraphics(true);
        ctrlSeparationMode.classList.add('selected');
    }  
    _animator.setSeparationMode(mode);
}
document.getElementById('btnModeWind').addEventListener('click', () => {
    selectSeparationMode(SEPARATION_MODE.WIND);
});
document.getElementById('btnModeDist').addEventListener('click', () => {
    selectSeparationMode(SEPARATION_MODE.DIST);
});

document.getElementById('btnModeWaypoint').addEventListener('click', () => {
    selectSeparationMode(SEPARATION_MODE.WAYPOINT);
});
function toggleTimeMode() {
    const enabled = btnModeTime.classList.toggle('selected');
    btnModeMeters.classList.toggle('selected');
    _animator.setShowTimeMode(enabled);
    if (enabled && _animator.separationMode == SEPARATION_MODE.DIST) {
        selectSeparationMode(SEPARATION_MODE.WIND);
    }
    btnModeDist.disabled = enabled;
    btnModeDist.classList.toggle('icon-button-disabled', enabled);
}
btnModeTime.addEventListener('click', () => {
    toggleTimeMode();
});

const btnModeMeters = document.getElementById('btnModeMeters');
btnModeMeters.addEventListener('click', () => {
    toggleTimeMode();
});

const ctrlMeasure = document.getElementById('ctrlMeasure');
ctrlMeasure.addEventListener('click', () => {
    if (_mapUIState.isDistToolSelected) {
        mapDist.removeInteractiveDistanceTool(map);
    } else {
        mapDist.addInteractiveDistanceTool(map);
    }
    _mapUIState.isDistToolSelected = !_mapUIState.isDistToolSelected;
    ctrlMeasure.classList.toggle('selected', _mapUIState.isDistToolSelected );
    console.log("Distance layer: ", _mapUIState.isDistToolSelected)
});

const ctrlRecords = document.getElementById('ctrlRecords');
ctrlRecords.addEventListener('click', () => {
    const newMode = !_animator.showRecords;
    _animator.setShowRecords(newMode);
    ctrlRecords.classList.toggle('selected', newMode);
});

const ctrlToggleManeuvers = document.getElementById('ctrlToggleManeuvers');
ctrlToggleManeuvers.addEventListener('click', () => {
    const enabled = ctrlToggleManeuvers.classList.toggle('selected');
    _animator.setShowManeuvers(enabled);
});

export function clearTimeWindow() {
    updateTimeWindow(moment(0),moment(0));
}

export function updateTimeWindow(startTs, endTs) {
    const bar = document.getElementById('timeWindowBar');
    if (startTs != null && endTs != null && endTs.isAfter(startTs) && _animator) {
        const leftPercent = calcProgressBarPct(startTs / 1000);
        const widthPercent = calcProgressBarPct(endTs / 1000) - leftPercent;
        bar.style.left = leftPercent + '%';
        bar.style.width = widthPercent + '%';
    } else {
        bar.style.left = '0%';
        bar.style.width = '0%';
    }
}

function calcProgressBarPct(tsSeconds) {
    return ((tsSeconds - _animator.firstTsSeconds) / (_animator.lastTsSeconds - _animator.firstTsSeconds)) * 100;
}

export function displayTimeWindow(flag) {
    document.getElementById('timeWindowBar').style.display = flag ? "flex" : "none";
}

export function skewedNormalize(x, min = 1, max = 20, power = 3) {
    let t = (x - min) / (max - min);
    return Math.pow(t, power);
}

export function inverseSkewedNormalize(y, min = 1, max = 20, power = 3) {
    const t = Math.pow(y, 1 / power);
    return min + t * (max - min);
}
